import { useState, useEffect, useRef, useCallback } from 'react';
import { Map } from '../Map';
import { BusMarkerComponent } from '../BusMarker';
import { api } from '../../lib/api';
import { getStoredToken } from '../../lib/auth';
import type { Bus, BusPosition } from '../../lib/types';

interface BusLiveData {
  bus: Bus;
  position: BusPosition | null;
  connected: boolean;
}

function useBusFleetTracker(buses: Bus[]) {
  const [busData, setBusData] = useState<Record<string, BusLiveData>>({});
  const wsRefs = useRef<Record<string, WebSocket>>({});
  const retryTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const connectBus = useCallback((bus: Bus) => {
    const token = getStoredToken();
    if (!token) return;

    // Close existing connection
    if (wsRefs.current[bus.id]) {
      wsRefs.current[bus.id].close();
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/gps/${bus.id}/ws?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRefs.current[bus.id] = ws;

    ws.onopen = () => {
      setBusData(prev => ({
        ...prev,
        [bus.id]: { ...prev[bus.id], bus, connected: true },
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'position') {
          setBusData(prev => ({
            ...prev,
            [bus.id]: {
              ...prev[bus.id],
              bus,
              connected: true,
              position: {
                lat: data.lat,
                lng: data.lng,
                speed: data.speed,
                heading: data.heading,
                accuracy: data.accuracy,
                ts: data.ts,
              },
            },
          }));
        }
      } catch { /* ignore */ }
    };

    ws.onclose = () => {
      setBusData(prev => ({
        ...prev,
        [bus.id]: { ...prev[bus.id], bus, connected: false },
      }));
      wsRefs.current[bus.id] = undefined!;
      // Retry after 5s
      retryTimers.current[bus.id] = setTimeout(() => connectBus(bus), 5000);
    };

    ws.onerror = () => {
      // onclose will fire after this
    };
  }, []);

  // Also poll REST position for each bus (initial load + fallback)
  const fetchPositions = useCallback(async (busList: Bus[]) => {
    const token = getStoredToken();
    if (!token) return;

    for (const bus of busList) {
      try {
        const res = await fetch(`/api/gps/${bus.id}/position`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as Record<string, number>;
          if (data.lat && data.lng) {
            setBusData(prev => {
              // Don't overwrite if we already have a newer WS position
              const existing = prev[bus.id];
              if (existing?.position && existing.position.ts > data.ts) return prev;
              return {
                ...prev,
                [bus.id]: {
                  bus,
                  connected: existing?.connected || false,
                  position: {
                    lat: data.lat,
                    lng: data.lng,
                    speed: data.speed || null,
                    heading: data.heading || null,
                    accuracy: data.accuracy || null,
                    ts: data.ts || Date.now(),
                  },
                },
              };
            });
          }
        }
      } catch { /* skip */ }
    }
  }, []);

  useEffect(() => {
    if (buses.length === 0) return;

    // Initialize bus data
    const initial: Record<string, BusLiveData> = {};
    buses.forEach(bus => {
      initial[bus.id] = { bus, position: null, connected: false };
    });
    setBusData(initial);

    // Fetch REST positions first, then open WebSockets
    fetchPositions(buses).then(() => {
      buses.forEach(bus => connectBus(bus));
    });

    return () => {
      // Cleanup
      Object.values(wsRefs.current).forEach(ws => ws?.close());
      Object.values(retryTimers.current).forEach(t => clearTimeout(t));
      wsRefs.current = {};
      retryTimers.current = {};
    };
  }, [buses, connectBus, fetchPositions]);

  return busData;
}

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function findNearestStop(lat: number, lng: number, stops: any[]): string | null {
  if (!stops.length) return null;
  let nearest = stops[0];
  let minDist = haversine(lat, lng, stops[0].latitude, stops[0].longitude);
  for (let i = 1; i < stops.length; i++) {
    const d = haversine(lat, lng, stops[i].latitude, stops[i].longitude);
    if (d < minDist) { minDist = d; nearest = stops[i]; }
  }
  return minDist < 5000 ? nearest.name : null; // Only show if within 5km
}

export function LiveStatus() {
  const [buses, setBuses] = useState<Bus[]>([]);
  const [stops, setStops] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusId, setSelectedBusId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.getBuses(),
      api.getStops().catch(() => ({ stops: [] })),
    ]).then(([busesRes, stopsRes]) => {
      setBuses(busesRes.buses);
      setStops(stopsRes.stops || []);
    }).catch((err) => console.error('LiveStatus: Failed to load:', err))
      .finally(() => setLoading(false));
  }, []);

  const busData = useBusFleetTracker(buses);
  const busEntries = Object.values(busData);
  const activeBuses = busEntries.filter(b => b.position !== null);
  const connectedCount = busEntries.filter(b => b.connected).length;

  // Map center: selected bus position, or first active bus, or default Delhi
  const selectedBus = selectedBusId ? busData[selectedBusId] : null;
  const mapCenter: [number, number] | undefined =
    selectedBus?.position
      ? [selectedBus.position.lat, selectedBus.position.lng]
      : activeBuses.length > 0
      ? [activeBuses[0].position!.lat, activeBuses[0].position!.lng]
      : undefined;

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="page-content">
      <div className="admin-section-header">
        <h1 className="page-title">Live Tracking</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="admin-count">{activeBuses.length} tracking</span>
          <span className={`connection-badge ${connectedCount > 0 ? 'badge-green' : 'badge-red'}`}>
            {connectedCount} connected
          </span>
        </div>
      </div>

      {/* Live Map */}
      <div style={{ height: 'calc(100vh - 280px)', minHeight: 400, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
        <Map center={mapCenter} zoom={13}>
          {busEntries.map(({ bus, position }) =>
            position ? (
              <BusMarkerComponent
                key={bus.id}
                position={position}
                busNumber={bus.bus_number}
                driverName={bus.driver_name}
                nearestStop={findNearestStop(position.lat, position.lng, stops)}
              />
            ) : null
          )}
        </Map>
      </div>

      {/* Bus List */}
      <div className="admin-cards">
        {buses.map((bus) => {
          const data = busData[bus.id];
          const pos = data?.position;
          const isConnected = data?.connected || false;
          const isSelected = selectedBusId === bus.id;
          const lastUpdate = pos?.ts ? new Date(pos.ts).toLocaleTimeString() : '--';
          const speedKmh = pos?.speed ? (pos.speed * 3.6).toFixed(0) : '0';
          const nearStop = pos ? findNearestStop(pos.lat, pos.lng, stops) : null;

          return (
            <div
              key={bus.id}
              className={`admin-card ${isSelected ? 'admin-card-selected' : ''}`}
              onClick={() => setSelectedBusId(bus.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="admin-card-header">
                <span className="admin-card-title">{bus.bus_number}</span>
                <span className={`admin-badge ${pos ? 'badge-green' : isConnected ? 'badge-yellow' : 'badge-red'}`}>
                  {pos ? 'Tracking' : isConnected ? 'Connected' : 'Offline'}
                </span>
              </div>
              <div className="admin-card-body">
                {nearStop && (
                  <div className="admin-card-row">
                    <span>Near</span>
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>{nearStop}</span>
                  </div>
                )}
                <div className="admin-card-row">
                  <span>Driver</span>
                  <span>{bus.driver_name || 'Unassigned'}</span>
                </div>
                {pos && (
                  <>
                    <div className="admin-card-row">
                      <span>Speed</span>
                      <span>{speedKmh} km/h</span>
                    </div>
                    <div className="admin-card-row">
                      <span>Position</span>
                      <span>{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</span>
                    </div>
                    <div className="admin-card-row">
                      <span>Last Update</span>
                      <span>{lastUpdate}</span>
                    </div>
                  </>
                )}
                {!pos && (
                  <div className="admin-card-row">
                    <span>Status</span>
                    <span style={{ color: 'var(--text-muted)' }}>No GPS data yet</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {buses.length === 0 && (
          <div className="admin-empty">No buses configured. Add one in the Buses tab.</div>
        )}
      </div>
    </div>
  );
}
